import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type { GeneralManagerFormValues } from '../schemas';
import type { DocumentUploadFile } from '@tasama/ui';

/**
 * Thin coordinator store for General Manager Information.
 *
 * The full validation logic has moved to react-hook-form inside GeneralManagerInformationForm.tsx.
 * This store exists only to:
 *  - Expose isValid to layout validation (continue button gating)
 *  - Expose formData for API payload building
 *  - Expose reset() for clearing stores when needed
 *  - Manage file upload state for passport copy (similar to upload-documents.store.ts)
 */

/**
 * In-memory registry for actual File objects keyed by their upload ID.
 * Not persisted — File objects are not serializable. Cleared on removal.
 */
export const passportFileRegistry = new Map<string, File>();

interface GeneralManagerInformationState extends BaseStoreState {
  formData: Partial<GeneralManagerFormValues>;
  isValid: boolean;
  pendingApiError: { field: string; message: string } | null;
  uploadedFiles: DocumentUploadFile[];
  passportFileId: string | null;
}

export interface GeneralManagerInformationStore extends GeneralManagerInformationState {
  setValidity: (_isValid: boolean) => void;
  setFormData: (_data: Partial<GeneralManagerFormValues>) => void;
  setFieldError: (_field: string, _message: string) => void;
  clearApiError: () => void;
  addFiles: (_files: DocumentUploadFile[]) => void;
  updateFile: (_fileId: string, _patch: Partial<DocumentUploadFile>) => void;
  removeFile: (_fileId: string) => void;
  setPassportFileId: (_fileId: string | null) => void;
  reset: () => void;
}

const initialFormData: Omit<GeneralManagerFormValues, 'passportCopy'> = {
  firstName: '',
  lastName: '',
  email: '',
  country: '+966',
  mobile: '',
  homeAddress: '',
  nationality: '',
};

export const useGeneralManagerInformationStore =
  createPersistedStore<GeneralManagerInformationState>(
    'general-manager-information',
    {
      formData: initialFormData,
      isValid: false,
      pendingApiError: null,
      uploadedFiles: [],
      passportFileId: null,
    },
    (set, get) => ({
      setValidity: (isValid: boolean) => set({ isValid }),

      setFormData: (data: Partial<GeneralManagerFormValues>) =>
        set((state: GeneralManagerInformationState) => ({
          formData: { ...state.formData, ...data },
        })),

      setFieldError: (field: string, message: string) =>
        set({ pendingApiError: { field, message } }),

      clearApiError: () => set({ pendingApiError: null }),

      addFiles: (files: DocumentUploadFile[]) => {
        const { uploadedFiles } = get();
        set({ uploadedFiles: [...uploadedFiles, ...files] });
      },

      updateFile: (fileId: string, patch: Partial<DocumentUploadFile>) => {
        const { uploadedFiles } = get();
        const updated = uploadedFiles.map((f: DocumentUploadFile) =>
          f.id === fileId ? { ...f, ...patch } : f
        );
        set({ uploadedFiles: updated });
      },

      removeFile: (fileId: string) => {
        const { uploadedFiles } = get();
        set({
          uploadedFiles: uploadedFiles.filter(
            (f: DocumentUploadFile) => f.id !== fileId
          ),
        });
      },

      setPassportFileId: (fileId: string | null) =>
        set({ passportFileId: fileId }),

      reset: () =>
        set({
          formData: initialFormData,
          isValid: false,
          pendingApiError: null,
          uploadedFiles: [],
          passportFileId: null,
        }),
    }),
    {
      partialize: (state: GeneralManagerInformationState) => ({
        formData: state.formData,
        isValid: state.isValid,
        uploadedFiles: state.uploadedFiles,
        passportFileId: state.passportFileId,
        // pendingApiError intentionally excluded — transient, not persisted
      }),
      clearOnRefresh: ['uploadedFiles', 'passportFileId'],
    }
  ) as unknown as PersistedStoreHook<GeneralManagerInformationStore>;

// Selectors — interfaces with layout validation and payload building
export const useGeneralManagerFormData = () =>
  useGeneralManagerInformationStore(
    (state: GeneralManagerInformationState) => state.formData
  );
export const useGeneralManagerValues = () =>
  useGeneralManagerInformationStore(
    (state: GeneralManagerInformationState) => state.formData
  );
export const useGeneralManagerErrors = () =>
  ({}) as Partial<Record<keyof GeneralManagerFormValues, string>>;
export const useGeneralManagerIsValid = () =>
  useGeneralManagerInformationStore(
    (state: GeneralManagerInformationState) => state.isValid
  );
export const useGeneralManagerUploadedFiles = () =>
  useGeneralManagerInformationStore(
    (state: GeneralManagerInformationState) => state.uploadedFiles
  );
